import { LinkIcon } from '@/assets/icons'
import Modal from '@components/Modal'
import { copyTextToClipboard } from '@lib/clipboard'
import { publicTripSectionUrl } from '@lib/publicLinks'
import React, { useState } from 'react'
import { toast } from 'react-hot-toast'
import styled from 'styled-components'

interface ShareItineraryModalProps {
	isOpen: boolean
	onClose: () => void
	tripCode: string
	isVisibleToPublic: boolean
}

const Description = styled.p`
	margin: 0 0 1.25rem 0;
	font-size: 0.95rem;
	color: #495057;
	line-height: 1.6;
`

const LinkRow = styled.div`
	display: flex;
	align-items: center;
	gap: 0.5rem;
	background: #f8f9fa;
	border: 1.5px solid #e9ecef;
	border-radius: 8px;
	padding: 0.625rem 0.75rem;
	margin-bottom: 1.25rem;
`

const LinkText = styled.span`
	flex: 1;
	font-size: 0.85rem;
	color: #212529;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	font-family: monospace;
`

const CopyButton = styled.button<{ $copied: boolean }>`
	flex-shrink: 0;
	padding: 0.375rem 0.875rem;
	border: none;
	border-radius: 6px;
	font-size: 0.8rem;
	font-weight: 600;
	cursor: pointer;
	transition: all 0.2s ease;
	background: ${props => props.$copied ? '#198754' : '#212529'};
	color: white;

	&:hover {
		opacity: 0.88;
	}
`

const VisibilityBanner = styled.div<{ $isPublic: boolean }>`
	display: flex;
	align-items: flex-start;
	gap: 0.6rem;
	padding: 0.75rem 1rem;
	border-radius: 8px;
	margin-bottom: 1.25rem;
	font-size: 0.875rem;
	line-height: 1.5;
	background: ${props => props.$isPublic ? '#d1e7dd' : '#fff3cd'};
	color: ${props => props.$isPublic ? '#0f5132' : '#664d03'};
	border: 1.5px solid ${props => props.$isPublic ? '#a3cfbb' : '#ffda6a'};
`

const StatusDot = styled.span<{ $isPublic: boolean }>`
	width: 8px;
	height: 8px;
	min-width: 8px;
	border-radius: 50%;
	margin-top: 5px;
	background: ${props => props.$isPublic ? '#198754' : '#f59e0b'};
`

const Hint = styled.p`
	margin: 0;
	font-size: 0.8rem;
	color: #6c757d;
	line-height: 1.5;
`

const ShareItineraryModal: React.FC<ShareItineraryModalProps> = ({
	isOpen,
	onClose,
	tripCode,
	isVisibleToPublic,
}) => {
	const [copied, setCopied] = useState(false)

	const publicUrl = publicTripSectionUrl(tripCode, 'itinerary')

	const handleCopy = async () => {
		try {
			await copyTextToClipboard(publicUrl)
			setCopied(true)
			setTimeout(() => setCopied(false), 2000)
		} catch (error) {
			toast.error('Could not copy the link. Please copy it manually.')
			console.error('Error copying public link:', error)
		}
	}

	return (
		<Modal isOpen={isOpen} onClose={onClose} title="Share Itinerary" closeOnBackdropClick size="medium">
			<Description>
				Anyone with this link can view your itinerary — no Orbit account required.
			</Description>

			<LinkRow>
				<LinkIcon sx={{ fontSize: 16 }} htmlColor="#6c757d" />
				<LinkText>{publicUrl}</LinkText>
				<CopyButton $copied={copied} onClick={handleCopy}>
					{copied ? 'Copied!' : 'Copy'}
				</CopyButton>
			</LinkRow>

			<VisibilityBanner $isPublic={isVisibleToPublic}>
				<StatusDot $isPublic={isVisibleToPublic} />
				{isVisibleToPublic
					? 'Your itinerary is public. Anyone with the link above can view it.'
					: 'Your itinerary is currently private — the link above will not work until you make it public.'}
			</VisibilityBanner>

			<Hint>
				You can toggle public visibility for this itinerary in your Trip Settings.
			</Hint>
		</Modal>
	)
}

export default ShareItineraryModal
